import React from "react";
import PropTypes from "prop-types";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  pageTitleClass,
  pageSubtitleClass,
  cardClass,
  cardHeaderClass,
  cardTitleClass,
  cardBodyClass,
  formLabelClass,
  infoGridClass,
  badgeClass,
} from "../dashboardStyles";
import ProfilePanel from "../ProfilePanel";
import NotificationPreferencesPanel from "../NotificationPreferencesPanel";
import LanguagePreferencesPanel from "../LanguagePreferencesPanel";
import DeleteAccountPanel from "../DeleteAccountPanel";
import ProfessionalAccountPanel from "../ProfessionalAccountPanel";
import TabList, { TabPanel } from "../TabList";
import { getUserInfoFromToken } from "../../../store/action";
import { UserRole } from "../../../constants/roles";
import { useNotificationSettings } from "../../../hooks/useNotificationSettings";
import {
  SETTINGS_TABS,
  NOTIFICATION_CHANNEL_TABS,
  SettingsTab,
  pickTab,
  withSettingsLocation,
} from "../../../constants/settings";

/**
 * The account card, the person's own details, and leaving — and, for a
 * personal account, the way to turn it into a business owner's.
 */
const AccountTab = ({ role }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-6">
      <div className={cardClass}>
        <div className={cardHeaderClass}>
          <h3 className={cardTitleClass}>{t("Dashboard.Settings.accountSettings")}</h3>
        </div>
        <div className={cardBodyClass}>
          <div className={infoGridClass}>
            <div className="min-w-0">
              <p className={formLabelClass}>{t("Dashboard.Settings.emailAddress")}</p>
              <p className="m-0 text-fluid-sm break-words text-ink-900">
                {localStorage.getItem("USER_EMAIL") || t("Dashboard.Settings.notSet")}
              </p>
            </div>
            <div>
              <p className={formLabelClass}>{t("Dashboard.Settings.accountStatus")}</p>
              <span className={`${badgeClass} bg-brand-50 text-brand-700`}>
                <i className="bi bi-check-circle-fill" aria-hidden="true" />
                {t("Dashboard.Settings.active")}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Directly under the account card it belongs to: these are the details
          that pre-fill a booking and seed the business search, so they sit
          with the identity rather than among the business settings. */}
      <ProfilePanel />

      {/* Customers only: the API refuses every other role, and an employee or
          a platform account becoming a business owner is not a thing. */}
      {role === UserRole.USER && <ProfessionalAccountPanel />}

      {/* Last, and visually separated: the only irreversible action here. */}
      <DeleteAccountPanel />
    </div>
  );
};

AccountTab.propTypes = {
  role: PropTypes.number,
};

/**
 * Settings: the account for everyone, and — for the members of a business —
 * what the business sends its customers and team, and in which language and
 * time zone.
 *
 * Customers and platform staff have no organization, so for them the page is
 * the account tab alone, without a tab strip. Employees see every tab, read
 * only; what customers receive is the owner's decision.
 *
 * The tab and the notification channel are read from the query string and
 * written back to it (constants/settings.js says why). Every tab stays mounted
 * and is only hidden, so an unsaved edit survives a look at another tab.
 */
const Settings = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const role = getUserInfoFromToken()?.role;
  const isOrganizationMember =
    role === UserRole.COMPANY_ADMIN || role === UserRole.COMPANY_EMPLOYEE;
  const canEdit = role === UserRole.COMPANY_ADMIN;

  const notificationSettings = useNotificationSettings(isOrganizationMember);

  const tab = pickTab(SETTINGS_TABS, searchParams.get("tab"));
  const channel = pickTab(NOTIFICATION_CHANNEL_TABS, searchParams.get("channel"));

  // Replace, not push: flicking through tabs should not fill the Back button.
  const go = (location) =>
    setSearchParams((current) => withSettingsLocation(current, location), {
      replace: true,
    });

  return (
    <div>
      <div className="mb-6">
        <h1 className={pageTitleClass}>{t("Dashboard.Settings.title")}</h1>
        <p className={pageSubtitleClass}>{t("Dashboard.Settings.subtitle")}</p>
      </div>

      {!isOrganizationMember ? (
        <AccountTab role={role} />
      ) : (
        <>
          <div className="mb-6">
            <TabList
              tabs={SETTINGS_TABS}
              active={tab}
              onChange={(next) => go({ tab: next, channel })}
              label={t("Dashboard.Settings.tabs.label")}
              idPrefix="settings"
            />
          </div>

          <TabPanel idPrefix="settings" id={SettingsTab.ACCOUNT} active={tab}>
            <AccountTab role={role} />
          </TabPanel>

          <TabPanel idPrefix="settings" id={SettingsTab.NOTIFICATIONS} active={tab}>
            <NotificationPreferencesPanel
              settings={notificationSettings}
              canEdit={canEdit}
              channel={channel}
              onChannelChange={(next) =>
                go({ tab: SettingsTab.NOTIFICATIONS, channel: next })
              }
            />
          </TabPanel>

          <TabPanel idPrefix="settings" id={SettingsTab.LANGUAGE} active={tab}>
            <LanguagePreferencesPanel settings={notificationSettings} canEdit={canEdit} />
          </TabPanel>
        </>
      )}
    </div>
  );
};

export default Settings;
